import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Calculator, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, errMsg, num, rupee, pnlClass } from "@/lib/api";

const Combo = ({ c, best, onPick, i, override }) => (
  <div data-testid={`planner-combo-${i}`} className={`flex flex-wrap items-center gap-3 rounded border px-3 py-2 text-xs ${best ? "border-emerald-500/40 bg-emerald-500/[0.06]" : "border-slate-800"}`}>
    {best && <span className="rounded bg-emerald-500/20 px-1.5 text-[10px] font-semibold text-emerald-300" data-testid="planner-best-badge">BEST</span>}
    <span className="font-semibold text-slate-100">{c.labels.join(" + ")}</span>
    <span className="num text-slate-400">margin {rupee(c.need)} · spare {rupee(c.spare)}</span>
    <span className="num text-slate-500">{c.basis === "backtest OOS PF" ? `OOS PF ${c.pfs.map((p) => (p == null ? "n/a" : p >= 999 ? "∞" : num(p))).join(" + ")}` : <>8-wk P&amp;L <b className={pnlClass(c.hist_pnl)}>{rupee(c.hist_pnl)}</b></>}</span>
    {c.gates.some((g) => !g) && (
      <span data-testid={`planner-gate-${i}`} className={`rounded px-1.5 text-[10px] font-semibold ${override ? "bg-amber-500/15 text-amber-300" : "bg-red-500/15 text-red-300"}`}>
        gate FAIL{override ? " · trades only via override" : " · skipped live"}
      </span>
    )}
    <Button data-testid={`button-planner-enable-${i}`} size="sm" variant="outline" onClick={() => onPick(c)} className="ml-auto h-6 border-slate-700 px-2 text-[11px]">Enable this combination</Button>
  </div>
);

export const CapitalPlanner = ({ onChange }) => {
  const [d, setD] = useState(null);
  const [whatIf, setWhatIf] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [pick, setPick] = useState(null);
  const load = useCallback(async (fresh = false, amount = whatIf) => {
    setBusy(true); setErr(null);
    try { setD((await api.get("/execution/planner", { params: { fresh, ...(amount ? { balance: Number(amount) } : {}) } })).data); }
    catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }, [whatIf]);
  useEffect(() => { load(false, ""); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const enable = async () => {
    try { await api.post("/execution/strategies/set", { kinds: pick.kinds }); toast.success(`Auto-trading now: ${pick.labels.join(" + ")}`); onChange?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setPick(null); }
  };
  return (
    <div className="panel col-span-12" data-testid="panel-capital-planner">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 px-4 py-2.5">
        <span className="eyebrow flex items-center gap-1.5"><Calculator size={12} /> Capital planner · what fits your balance</span>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">What if balance ₹</span>
          <input data-testid="input-planner-whatif" type="number" min={0} step={10000} placeholder={d ? String(Math.round(d.real_balance)) : "real"} value={whatIf}
            onChange={(e) => setWhatIf(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(false)}
            className="h-7 w-28 rounded border border-slate-700 bg-slate-900 px-2 text-right text-[11px] text-slate-100 focus:border-indigo-500 focus:outline-none" />
          <Button data-testid="button-planner-run" size="sm" variant="ghost" disabled={busy} onClick={() => load(false)} className="h-7 text-xs text-slate-300">Plan</Button>
          {whatIf && <Button data-testid="button-planner-real" size="sm" variant="ghost" disabled={busy} onClick={() => { setWhatIf(""); load(false, ""); }} className="h-7 text-xs text-slate-500">Use real</Button>}
          <Button data-testid="button-planner-refresh" size="sm" variant="ghost" disabled={busy} onClick={() => load(true)} className="h-7 gap-1 text-xs text-slate-400">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> {busy ? "Asking Dhan…" : "Re-price"}
          </Button>
        </div>
      </div>
      {err && <p className="px-4 py-3 text-xs text-amber-400" data-testid="planner-error">{err}</p>}
      {!d && !err && <p className="px-4 py-4 text-sm text-slate-500">Pricing today's margins…</p>}
      {d && (
        <div className="space-y-3 p-4">
          <p className="text-xs text-slate-400" data-testid="planner-balance">
            Planning with <b className="text-slate-100">{rupee(d.balance)}</b> {d.what_if ? <span className="text-amber-300">(what-if; real {rupee(d.real_balance)})</span> : "(your Dhan balance)"}.
            Combinations are up to {d.max_open} strategies that can't clash, sized by peak margin (both open at once). Ranked by 8-week live + dry P&amp;L, or by backtest OOS profit factor when there's no history yet (strategies with fewer than 30 OOS trades count against a combination; n/a). Strategies with a backtest PF below 1 are never paired.
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="planner-strategies">
            {d.strategies.map((x) => (
              <span key={x.kind} data-testid={`planner-fit-${x.kind}`} className={`num rounded border px-2 py-0.5 text-[11px] ${x.need <= d.balance ? "border-emerald-500/40 text-emerald-300" : "border-slate-800 text-slate-500"}`}>
                {x.label} {rupee(x.need)}
              </span>
            ))}
          </div>
          {d.combos.length === 0 ? (
            <p className="rounded border border-red-500/30 bg-red-500/[0.06] px-3 py-2 text-xs text-red-300" data-testid="planner-none">
              Nothing fits {rupee(d.balance)}. The cheapest strategy needs {rupee(Math.min(...d.strategies.map((x) => x.need)))}. Add funds or try a what-if amount.
            </p>
          ) : d.combos.map((c, i) => <Combo key={c.kinds.join("+")} c={c} i={i} best={i === 0} onPick={setPick} override={d.override} />)}
        </div>
      )}
      <AlertDialog open={!!pick} onOpenChange={(o) => !o && setPick(null)}>
        <AlertDialogContent data-testid="dialog-planner-confirm" className="border-slate-700 bg-[#0D121F] text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Auto-trade only {pick?.labels.join(" + ")}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This replaces your enabled strategy list with exactly these. When live trading is armed they place REAL Dhan orders on their own signals (peak margin {rupee(pick?.need)}). All other strategies are switched off. Every other limit stays the same.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-planner-cancel" className="border-slate-700 bg-transparent">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="button-planner-confirm" onClick={enable} className="bg-indigo-500 hover:bg-indigo-400">Enable these</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
